import { useState, useEffect } from "react";
import { AlertTriangle, Clock, Phone, CalendarClock } from "lucide-react";
import Modal from "../components/Modal";
import { fmtShort, getToday, generateId } from "../utils";
import {
  MOTIVOS_PARADO, PRAZO_ABORDAGEM_DIAS,
  diasParado, ultimoMovimento, aplicarJustificativa, somarDias, ultimoComprovante,
} from "../lib/abordagem";

// ═══════════════════════════════════════════════════════════════
// COBRANÇA DE ABORDAGEM PARADA
// ═══════════════════════════════════════════════════════════════
//
// Aparece quando existe empresa em abordagem há mais de 2 dias sem contato
// registrado. Uma por vez, com o nome na frente: lista grande vira paisagem,
// e o ponto aqui é que cada silêncio tenha dono e explicação.
//
// A justificativa NÃO muda o estágio. Continua em abordagem, esperando o
// parceiro — só passa a ter prazo, autor e próxima tentativa agendada.

export default function CobrancaAbordagem({
  open, pendentes = [], setPartners, setTasks, userId, userName = "",
  onRegistrarContato, onAdiar,
}) {
  const hoje = getToday();
  const [i, setI] = useState(0);
  const [motivo, setMotivo] = useState("");
  const [detalhe, setDetalhe] = useState("");
  const [proxima, setProxima] = useState("");
  const [erro, setErro] = useState("");

  const atual = pendentes[Math.min(i, Math.max(pendentes.length - 1, 0))] || null;

  // Limpa o formulário sempre que troca de empresa: motivo de uma não pode
  // vazar para a próxima e virar justificativa em série.
  useEffect(() => {
    setMotivo(""); setDetalhe(""); setErro("");
    setProxima(somarDias(hoje, 2));
  }, [atual?.id, hoje]);

  useEffect(() => { if (open) setI(0); }, [open]);

  if (!open || !atual) return null;

  const parado = diasParado(atual, hoje);
  const desde = ultimoMovimento(atual);
  const prova = ultimoComprovante(atual);
  const nunca = !(atual.interactions || []).length;

  const salvar = () => {
    if (!motivo) { setErro("Escolha o que aconteceu."); return; }
    if (motivo === "outro" && !detalhe.trim()) { setErro("Descreva o motivo."); return; }
    if (!proxima || proxima <= hoje) { setErro("A próxima tentativa precisa ser uma data futura."); return; }

    setPartners((prev) => prev.map((p) => (
      p.id === atual.id ? aplicarJustificativa(p, { motivo, detalhe, proximaTentativa: proxima, autor: userName }, hoje) : p
    )));

    // Vira tarefa no Painel do Dia: a promessa de tentar de novo só vale se
    // reaparecer na data em que foi prometida.
    setTasks((prev) => [...prev, {
      id: generateId(), title: `Nova tentativa de contato — ${atual.name}`,
      type: "partner", refId: atual.id, userId,
      dueDate: proxima, priority: "high", done: false,
      notes: `Abordagem parada há ${parado} dias. ${MOTIVOS_PARADO.find((m) => m.id === motivo)?.label || ""}${detalhe.trim() ? ` — ${detalhe.trim()}` : ""}`,
    }]);

    if (i + 1 < pendentes.length) setI(i + 1); else onAdiar();
  };

  const rot = { display: "flex", alignItems: "center", gap: 8, padding: "8px 11px", borderRadius: 7, fontSize: 12, cursor: "pointer", border: "1px solid #e2e8f0", background: "#fff", color: "#475569" };
  const rotAtivo = { ...rot, border: "1px solid #dc2626", background: "#fef2f2", color: "#991b1b", fontWeight: 600 };

  return (
    <Modal
      open={open}
      onClose={onAdiar}
      closeOnBackdrop={false}
      title={pendentes.length > 1 ? `Abordagem parada (${i + 1} de ${pendentes.length})` : "Abordagem parada"}
      w={560}
    >
      <div style={{ display: "flex", gap: 9, padding: "11px 13px", borderRadius: 9, background: "#fef2f2", border: "1px solid #fecaca", marginBottom: 14 }}>
        <AlertTriangle size={16} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 11.5, color: "#991b1b", lineHeight: 1.5 }}>
          Empresa em <strong>abordagem há mais de {PRAZO_ABORDAGEM_DIAS} dias</strong> sem contato registrado.
          Explique o que houve — ela <strong>continua em abordagem</strong>, esperando resposta do parceiro.
        </div>
      </div>

      <div style={{ padding: "12px 14px", borderRadius: 9, background: "#f8fafc", border: "1px solid #e2e8f0", marginBottom: 14 }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, color: "#0f172a" }}>{atual.name}</div>
        <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
          {atual.contact ? `${atual.contact} · ` : ""}{atual.phone || "sem telefone"}
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8, fontSize: 11, color: "#475569" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 4, color: "#b91c1c", fontWeight: 700 }}>
            <Clock size={11} /> {parado} {parado === 1 ? "dia" : "dias"} parado
          </span>
          {desde && <span>Último movimento: {fmtShort(desde)}</span>}
        </div>
        <div style={{ fontSize: 10.5, marginTop: 6, color: nunca ? "#b91c1c" : "#475569" }}>
          {nunca
            ? "Nunca teve contato registrado — está em abordagem sem nenhum registro."
            : prova
              ? `Último contato com print anexado em ${fmtShort(prova.date)}.`
              : "Contatos registrados, mas nenhum com print anexado."}
        </div>
      </div>

      <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 7 }}>O que aconteceu?</div>
      <div style={{ display: "grid", gap: 5, marginBottom: 12 }}>
        {MOTIVOS_PARADO.map((m) => (
          <div key={m.id} onClick={() => { setMotivo(m.id); setErro(""); }} style={motivo === m.id ? rotAtivo : rot}>
            <input type="radio" readOnly checked={motivo === m.id} style={{ pointerEvents: "none" }} />
            {m.label}
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 5 }}>
          Detalhe {motivo === "outro" ? "(obrigatório)" : "(opcional)"}
        </div>
        <textarea
          value={detalhe}
          onChange={(e) => { setDetalhe(e.target.value); setErro(""); }}
          placeholder="Ex: mandei 2 mensagens dia 10 e 11, visualizou e não respondeu"
          style={{ width: "100%", minHeight: 54, resize: "vertical", padding: "8px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 12, fontFamily: "inherit", boxSizing: "border-box", color: "#334155" }}
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 5 }}>
          <CalendarClock size={12} /> Quando você tenta de novo?
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="date" value={proxima} min={somarDias(hoje, 1)}
            onChange={(e) => { setProxima(e.target.value); setErro(""); }}
            style={{ padding: "7px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 12, background: "#fff", color: "#334155" }}
          />
          {[1, 2, 5, 7].map((d) => (
            <button key={d} onClick={() => { setProxima(somarDias(hoje, d)); setErro(""); }} style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
              {d === 1 ? "amanhã" : `${d} dias`}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 5 }}>
          Vira tarefa no Painel do Dia nessa data, e o alerta só volta se a data passar sem contato.
        </div>
      </div>

      {erro && (
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#dc2626", marginBottom: 10 }}>
          <AlertTriangle size={12} /> {erro}
        </div>
      )}

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end", borderTop: "1px solid #f1f5f9", paddingTop: 12 }}>
        <button onClick={onAdiar} style={{ padding: "8px 14px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer", marginRight: "auto" }}>
          Agora não
        </button>
        <button
          onClick={() => onRegistrarContato && onRegistrarContato(atual.id)}
          style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 14px", borderRadius: 7, border: "1px solid #a7f3d0", background: "#f0fdfa", color: "#0f766e", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
        >
          <Phone size={12} /> Falei com ele agora
        </button>
        <button onClick={salvar} style={{ padding: "8px 16px", borderRadius: 7, border: "none", background: "#dc2626", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          {i + 1 < pendentes.length ? "Justificar e continuar" : "Justificar"}
        </button>
      </div>
    </Modal>
  );
}
